import React, { Fragment, useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Dialog, Transition } from '@headlessui/react';
import { RootState, useAppDispatch } from '../../store';
import { hideModal } from '../../store/actions/modalActions';
import { Citation } from '../../store/actions/ChatAction';
import { useTranslation } from 'react-i18next';
import apis from '../../utils/apis';
import SignatureModal from '../Settings/Security/DigitalSignature';
import AddTask from '../Tasks/AddTaskComponent';
import BankFormItems from '../Settings/Security/BankFormItems';
import Input from '../shared/TailwindComponents/Input';
import Button from '../shared/TailwindComponents/Button';
import Textarea from '../shared/TailwindComponents/Textarea';
import { CheckIcon, XCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import VaultPicker, { VaultFile } from './TailwindComponents/VaultPicker';
import TimeStopModalContent from '../TimeTracker/TimeStopModalContent';
import SuggestedTasksModal from '../Tasks/SuggestedTasksModal';
import AttachCaseToClientModalContent from '../Clients/AttachCaseToClientModalContent';
import ConnectMatterModal from '../Clients/ConnectMatterModal';
import { AiOutlineClose, AiOutlineInfoCircle } from "react-icons/ai";
import { PiSpinnerGapLight } from "react-icons/pi";
import { FaArrowRight } from "react-icons/fa";
import AISettingsComponent from '../Settings/SettingsTabs/AISettings';
import ImportClientsModal from '../Clients/ImportClientsModal';
import SendOnboardingEmailsModal from '../Clients/SendOnboardingEmailModal';
import AddSourceModalContent from './AddSourceModalContent';
import FileUploader from './TailwindComponents/FileUploader';
import FileReaderComponent from './FileReader';
import CustomFileReader from './CustomFileReader';
import EditCaseDataModalContent from './EditCaseDataContent';
import ReviewHelpGuide from '../Review/ReviewHelpGuide';
import PlaybookHelpGuide from '../Review/PlaybookHelpGuide';
import ImportSummaryModal from '../Clients/ImportSummaryModal';
import { ImportSummary } from '../../store/reducer/modalReducer';
import { getSignature } from '../../store/actions/DocumentsAction';
import EditDraftInstructionModal from './Drafts/EditDraftInstructionsModal';
import EditTextModal from '../Templates/EditTextModal';
import ShareAccessManager from './ShareAccessManager';
import CollaboratorSelector from './CollaboratorSelector';
import ManageAccessModal from '../Files/ManageAccessModal';
import TeamActionModal from '../Management/Teams/TeamActionModal';
import CreateFolder from '../Files/CreateFolder';
import CreateFolderOnly from '../Files/CreateFolderOnly';
import EditDepartmentModal, { EditDepartmentModalHandle } from '../Management/Departments/EditDepartmentModal';
import EditTeamModal, { EditTeamModalHandle } from '../Management/Teams/EditTeamModal';
import EditRateOverrideModal, { EditRateOverrideModalHandle } from '../Matters/modals/EditRateOverrideModal';
import EditTimeEntryModal, { EditTimeEntryModalHandle } from '../Matters/modals/EditTimeEntryModal';
import OutcomeModal, { OutcomeModalHandle } from '../Matters/modals/OutcomeModal';
import { handleSendOnboardingEmails } from '../../store/actions/ClientActions';
import { DocumentUploadModalContent } from '../Clients/DocumentUploadModal';
import AdminRoleModal from '../Admin/modals/AdminRoleModal';
import AdminRoleDetailModal from '../Admin/modals/AdminRoleDetailModal';
import AdminUserModal from '../Admin/modals/AdminUserModal';
import AdminInviteUserModal from '../Admin/modals/AdminInviteUserModal';
import AddClientToMatterModal from '../Matters/AddClientToMatterModal';
import VersionHistory from '../Files/documents/VersionHistory';
import DocumentInfoModal from '../Files/documents/DocumentInfoModal';
import LicenseGateModal from '../Settings/Licensing/LicenseGateModal';
import TemplateCollaboratorsModalContent from '../Templates/TemplateCollaboratorsModalContent';
import TemplatePreviewModalContent from '../Templates/TemplatePreviewModalContent';
import AddTableColumnContent from '../BatchTables/AddTableColumnContent';
import EditColumnContent from '../BatchTables/EditColumnContent';
import TagPickerModal from '../Files/TagPickerModal';
import AuditLogDetailModal from '../AuditLog/AuditLogDetailModal';
import DraftingMatterPickerModal from '../Drafting/DraftingMatterPickerModal';
import ResearchShareModal from '../Research/ResearchShareModal';
import PlaybookPickerModal from '../BatchTables/PlaybookPickerModal';
import TemplatePickerModal from '../BatchTables/TemplatePickerModal';
import Spinner from './TailwindComponents/Spinner';


const GlobalModal: React.FC = () => {
  const modalState = useSelector((state: RootState) => state.modal);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [inputData, setInputData] = useState('');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [completeTask, setCompleteTask] = useState(false);
  const [comment, setComment] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [editDepartmentSaving, setEditDepartmentSaving] = useState(false);
  const [editTeamSaving, setEditTeamSaving] = useState(false);
  const [selectedMatterId, setSelectedMatterId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [connectMatterLoading, setConnectMatterLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);


  const editDepartmentRef = useRef<EditDepartmentModalHandle>(null);
  const editTeamRef = useRef<EditTeamModalHandle>(null);
  const editRateOverrideRef = useRef<EditRateOverrideModalHandle>(null);
  const [editRateOverrideSaving, setEditRateOverrideSaving] = useState(false);
  const [editRateOverrideValid, setEditRateOverrideValid] = useState(false);
  const editTimeEntryRef = useRef<EditTimeEntryModalHandle>(null);
  const [editTimeEntrySaving, setEditTimeEntrySaving] = useState(false);
  const [editTimeEntryValid, setEditTimeEntryValid] = useState(false);
  const outcomeRef = useRef<OutcomeModalHandle>(null);
  const [outcomeSaving, setOutcomeSaving] = useState(false);
  const [outcomeValid, setOutcomeValid] = useState(false);

  // ── Canonical payload helpers (#2332) ──
  // Headline shows `title`; legacy callers fall back to `message`. Secondary text
  // is `subText`, falling back to the legacy `subMsg`.
  /*
    `license-request` DRAWS ITS OWN CARD.

    `LicenseGateModal` is a complete thing - lock icon, heading, the module and
    its price, the actions - and the shell's title bar put a SECOND heading
    above it with a "Close" button beside it. That is why the same lock read as
    two different components: the page overlay showed the card, and an
    API-403 showed the card under a header saying "Analytics · Close".
  */
  const suppressShellHeader = modalState.type === 'license-request';
  const modalHeadline = suppressShellHeader
    ? null
    : modalState.title || modalState.message;
  const modalSecondary = modalState.subText || modalState.subMsg;
  const variantBandClass =
    modalState.variant === 'info' ? 'border-t-4 border-info-500' :
    modalState.variant === 'warning' ? 'border-t-4 border-warning-500' :
    modalState.variant === 'danger' ? 'border-t-4 border-danger-500' :
    modalState.variant === 'success' ? 'border-t-4 border-success-500' : '';
  const isDanger = modalState.variant === 'danger' || modalState.isDest;

  // Dev-only deprecation warning for legacy (pre-#2332) payloads on the simple
  // message modals, emitted once per payload. Unblocks the #2333 migration.
  const warnedPayloads = useRef<WeakSet<object>>(new WeakSet());
  useEffect(() => {
    if (process.env.NODE_ENV === 'production' || !modalState.showModal) return;
    const CANONICAL_TYPES = ['confirmation', 'alert', 'info', 'warning', 'error', 'success'];
    if (!CANONICAL_TYPES.includes(modalState.type) || modalState.title) return;
    if (warnedPayloads.current.has(modalState)) return;
    warnedPayloads.current.add(modalState);
    // eslint-disable-next-line no-console
    console.warn(
      `[GlobalModal] Legacy modal payload (type="${modalState.type}") has no "title". ` +
      'Use the canonical shape { title, message, onConfirm, subText?, icon?, variant? } - ' +
      'see CLAUDE.md §6.1. Falling back to "message" as the title (#2332).',
    );
  }, [modalState]);

  /** Reset local field state and dismiss - without firing the cancel path. */
  const dismiss = useCallback(() => {
    setInputData('');
    setCompleteTask(false);
    setFiles([]);
    setEditDepartmentSaving(false);
    setEditTeamSaving(false);
    dispatch(hideModal());
  }, [dispatch]);

  const handleClose = useCallback(() => {
    dismiss();
    if (modalState.onCancel) {
      modalState.onCancel();
    }
    // Canonical explicit close handler (#2332), runs in addition to onCancel.
    if (modalState.onClose) {
      modalState.onClose();
    }
  }, [dismiss, modalState]);


  const handleConfirm = useCallback((data?: any) => {
    if (modalState.onConfirm) {
      if (modalState.type === 'uploadAttachment' && files.length > 0) {
        modalState.onConfirm(files);
      } else if (modalState.type === 'uploadTableDocuments' && files.length > 0) {
        modalState.onConfirm(files);
      } else if (modalState.type === 'timeStop') {
        modalState.onConfirm(inputData, completeTask);
      } else if (modalState.type === 'addSignature') {
        const signatureBlob = new Blob([inputData], { type: 'image/svg+xml' });
        const signatureFile = new File([signatureBlob], 'signature.svg', { type: 'image/svg+xml' });
        modalState.onConfirm(signatureFile);
      } else if (modalState.type === 'addTextContent') {
        modalState.onConfirm(inputData);
      } else if (['share', 'sign', 'password', 'promptfeedback', 'confirmation'].includes(modalState.type)) {
        modalState.onConfirm(inputData);
      } else if (modalState.type === 'forgotPassword') {
        modalState.onConfirm(modalState.inputData ? modalState.inputData : email);
        setEmail('');
      } else if (modalState.type === '2fa-qrcode') {
        modalState.onConfirm(verificationCode);
        setVerificationCode('');
      } else if (modalState.type === 'alert') {
        modalState.onConfirm(modalState.inputData ? modalState.inputData : '');
      } else if (modalState.type === 'confirm-upload') {
        modalState.onConfirm(inputData);
      } else if (modalState.type === 'addTaskComment') {
        modalState.onConfirm(comment.trim());
      } else {
        modalState.onConfirm(data)
      }
    }
    if (modalState.type !== '2fa-qrcode') {
      handleClose();
    }
  }, [completeTask, email, handleClose, inputData, modalState, verificationCode, comment, files]);

  useEffect(() => {
    if (!modalState.showModal) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        const target = event.target as HTMLElement;
        const isTextarea = target.tagName === 'TEXTAREA';
        // The playbook / template pickers own their confirm button; a global
        // Enter would close them out from under the user while they type in
        // their search box.
        if (!isTextarea && modalState.type !== 'playbookPicker' && modalState.type !== 'templatePicker') {
          handleConfirm();
        }
      } else if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [modalState.showModal, modalState.type, handleConfirm, handleClose]);

  const getIcon = () => {
    switch (modalState.type) {
      case 'success':
        return (
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-success-100 dark:bg-success-800">
            <CheckIcon className="h-6 w-6 text-success-600 dark:text-success-100" aria-hidden="true" />
          </div>
        );
      case 'error':
        return (
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-danger-100 dark:bg-danger-800">
            <XCircleIcon className="h-6 w-6 text-danger-600 dark:text-danger-100" aria-hidden="true" />
          </div>
        );
      case 'warning':
        return (
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-warning-100 dark:bg-warning-800">
            <ExclamationCircleIcon className="h-6 w-6 text-warning-600 dark:text-warning-100" aria-hidden="true" />
          </div>
        );
      default:
        return null;
    }
  };

  const handleVaultFilesSelected = useCallback(async (selectedFiles: VaultFile[]) => {
    if (modalState.onFilesChange) {
      const newFileList: File[] = [];
      for (const item of selectedFiles) {
        if (item.url) {
          try {
            const response = await fetch(item.url);
            if (!response.ok) {
              if (response.status === 403) {
                console.warn('SAS token may be expired. Please refresh the token via the vault API.');
              }
              throw new Error(`Failed to fetch ${item.name}: ${response.statusText}`);
            }
            const blob = await response.blob();
            const mimeType = response.headers.get('Content-Type') || 'application/octet-stream';
            const newFile = new File([blob], item.name, { type: mimeType });
            newFileList.push(newFile);
          } catch (err) {
            console.error(`Error processing ${item.name}:`, err);
          }
        }
      }
      const updatedFiles = [...(modalState.files || []), ...newFileList];
      modalState.onFilesChange(updatedFiles);
    } else if (modalState.onConfirm) {
      modalState.onConfirm(selectedFiles);
    } else {
    }
    handleClose();
  }, [modalState, handleClose]);



  // The playbook picker brings its own backdrop and full-height panel, so it
  // renders INSTEAD of the Dialog chrome rather than inside it - nesting it
  // would stack two backdrops and wrap a 85vh panel in a 500px-min dialog.
  if (modalState.showModal && modalState.type === 'playbookPicker') {
    return (
      <PlaybookPickerModal
        selectedPlaybookUuid={modalState.selectedPlaybookUuid || ''}
        onSelect={(playbook) => modalState.onPlaybookSelect?.(playbook)}
        onClose={handleClose}
      />
    );
  }

  // Same deal for the template picker - and, like the playbook one, it has to
  // render from HERE rather than from the form that opens it: the create-table
  // form's fields live inside a `.glass` card, and `backdrop-filter` makes that
  // card the containing block for `position: fixed` children. Rendered inline
  // the picker was laid out and clipped inside the card, with its z-index
  // trapped in the card's stacking context, so its cards could not be clicked.
  if (modalState.showModal && modalState.type === 'templatePicker') {
    return (
      <TemplatePickerModal
        templates={modalState.templates || []}
        selectedTemplateId={modalState.selectedTemplateId || ''}
        onSelect={(templateId) => modalState.onTemplateSelect?.(templateId)}
        onClose={handleClose}
      />
    );
  }

  return (
    <Transition.Root show={modalState.showModal} as={Fragment}>
      <Dialog as="div" className="relative z-[300000000]" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-ink/50 backdrop-blur-[2px] transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-[3000] overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={`relative transform overflow-visible rounded-lg bg-surface border border-hairline pb-4 pt-6 text-start shadow-xl transition-all ease-pop sm:my-8 min-w-[500px] ${variantBandClass}`}
                style={modalState.width ? { width: modalState.width, minWidth: 'auto' } : undefined}
              >
                <div className='flex justify-between items-center px-4 pb-6 border-b border-hairline-soft flex-1'>
                  {modalHeadline && (
                    <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 text-start w-full">
                      <div className='flex flex-row justify-between items-center gap-5'>
                        <div className='flex-row flex gap-2 items-center'>
                          {modalState.icon}
                          {t(modalHeadline)}
                        </div>
                        <Button
                          variant="neutral"
                          size="small"
                          onClick={handleClose}
                          className="px-4"
                        >
                          {t("Close")}
                        </Button>
                      </div>
                      {modalSecondary && modalState.type !== 'confirmation' && (
                        <div className="mt-4">
                          <p className="text-sm max-w-md text-gray-500 dark:text-gray-300" dangerouslySetInnerHTML={{ __html: t(modalSecondary) }}></p>
                        </div>
                      )}
                    </Dialog.Title>
                  )}
                </div>
                {getIcon()}

                <div className="mt-3 text-center sm:mt-5">

                  {modalState.type === 'citations' && (
                    <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                      {t('Citations')}
                    </Dialog.Title>
                  )}
                </div>

                <div className="mt-5 sm:mt-6">
                  {modalState.type === 'create' && (

                    <CreateFolder
                      existingFolders={modalState.folderOptions ? modalState.folderOptions : []}
                      selectedFolderItem={modalState.item}
                      onClose={handleClose}
                      handleCreateFolder={handleConfirm}
                      employees={modalState.employees}
                      initialShared={modalState.initialShared}
                      onRefresh={modalState.onRefresh}

                    />

                  )}

                  {modalState.type === 'create_folder' && (
                    <CreateFolderOnly
                      existingFolders={modalState.folderOptions ? modalState.folderOptions : []}
                      selectedFolderItem={modalState.item}
                      onClose={handleClose}
                      handleCreateFolder={handleConfirm}
                      employees={modalState.employees}
                      initialShared={modalState.initialShared}
                      onRefresh={modalState.onRefresh}
                    />
                  )}

                  {modalState.type === 'declineEngagement' && (
                    <div className="p-4 max-w-md mx-auto">
                      <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder={t('Please provide a reason for declining')}
                        className="w-full h-24 p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-info-500"
                      />
                      <div className="mt-4 flex justify-end gap-2">
                        <Button
                          variant="primary"
                          size="small"
                          onClick={handleConfirm}
                          disabled={!comment.trim()}
                        >
                          {t('Submit')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {modalState.type === 'signEngagement' && (
                    <div className="p-4 max-w-lg mx-auto">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                        {t('Sign Engagement Letter')}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-300 mb-4">
                        {t('Review or update your signature below, then click "Sign and Accept" to proceed.')}
                      </p>
                      <SignatureModal
                        onSave={() => { }}
                        onConfirm={() => { }}
                      />
                      <div className="mt-4 flex justify-end gap-2">
                        <Button
                          variant="primary"
                          size="small"
                          onClick={async () => {
                            const signatureUrl = await dispatch(getSignature());
                            if (signatureUrl) {
                              const response = await fetch(signatureUrl);
                              const blob = await response.blob();
                              const signatureFile = new File([blob], 'signature.svg', { type: 'image/svg+xml' });
                              if (modalState.onConfirm) {
                                modalState.onConfirm(signatureFile);
                              }
                              handleClose();
                            } else {
                              alert(t('No signature found. Please create and save a signature first.'));
                            }
                          }}
                        >
                          {t('Sign and Accept')}
                        </Button>
                      </div>
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-300">
                        {t('Once signed, your signature will be embedded into the engagement letter, and the lawyer will be notified.')}
                      </p>
                    </div>
                  )}

                  {modalState.type === 'signFinalEngagement' && (
                    <div className="p-4 max-w-lg mx-auto">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                        {t('Sign to Finalize Engagement Letter')}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-300 mb-4">
                        {t('Review or update your signature below, then click "Sign and Finalize" to proceed.')}
                      </p>
                      <SignatureModal
                        onSave={() => { }}
                        onConfirm={() => { }}
                      />
                      <div className="mt-4 flex justify-end gap-2">
                        <Button
                          variant="primary"
                          size="small"
                          onClick={async () => {
                            const signatureUrl = await dispatch(getSignature());
                            if (signatureUrl) {
                              const response = await fetch(signatureUrl);
                              const blob = await response.blob();
                              const signatureFile = new File([blob], 'signature.svg', { type: 'image/svg+xml' });
                              if (modalState.onConfirm) {
                                modalState.onConfirm(signatureFile);
                              }
                              handleClose();
                            } else {
                              alert(t('No signature found. Please create and save a signature first.'));
                            }
                          }}
                        >
                          {t('Sign and Finalize')}
                        </Button>
                      </div>
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-300">
                        {t('Once signed, your signature will be embedded into the engagement letter, and it will be marked as final.')}
                      </p>
                    </div>
                  )}

                  {modalState.type === 'editDraftInstruction' && (
                    <EditDraftInstructionModal
                      initialInstruction={modalState.inputData || ''}
                      onConfirm={modalState.onConfirm || (() => { })}
                      onCancel={modalState.onCancel || (() => { })}
                    />
                  )}

                  {/* Description, Tone and Writing Style are one editor with
                      three headlines - they were three identical blocks, and
                      the next attribute would have been a fourth. */}
                  {(modalState.type === 'editDescription'
                    || modalState.type === 'editTone'
                    || modalState.type === 'editWritingStyle') && (
                    <EditTextModal
                      title={modalState.message}
                      initialValue={modalState.inputData || ''}
                      onConfirm={modalState.onConfirm || (() => { })}
                      onCancel={modalState.onCancel || (() => { })}
                      rows={modalState.rows}
                      placeholder={modalState.placeholder}
                    />
                  )}

                  {modalState.type === 'uploadAttachment' && (
                    <div className='w-[50vw] max-w-[50vw] min-w-[50vw] relative'>
                      <div className='px-4'>
                        <FileUploader
                          files={files}
                          onFilesChange={(newFiles) => setFiles(newFiles)}
                        />
                      </div>
                      <div className="mt-6 pt-6 border-t border-1 border-gray-300 flex justify-end gap-2 px-4">
                        <Button
                          variant="primary"
                          size="small"
                          onClick={() => handleConfirm()}
                          disabled={files.length === 0}
                        >
                          {t('Upload')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {modalState.type === 'imageViewer' && (
                    <div className="p-4">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{modalState.filename}</h3>
                      <img src={modalState.imageUrl} alt={modalState.filename} className="max-w-full h-auto" />
                    </div>
                  )}

                  {modalState.type === 'fileReader' && (
                    <FileReaderComponent
                      fileUrl={modalState.fileUrl!}
                      fileType={modalState.fileType!}
                    />
                  )}
                  {modalState.type === 'fileReaderTst' && (
                    <CustomFileReader fileUrl={modalState.fileUrl} filename={modalState.filename!} />
                  )}

                  {modalState.type === 'attachmentViewer' && modalState.component && (
                    <div className="w-[90vw] h-[78vh] flex flex-col">
                      {modalState.component}
                    </div>
                  )}

                  {modalState.type === 'onboarding-email' && (
                    <SendOnboardingEmailsModal
                      selectedClients={modalState.selectedClients || []}
                      onClose={() => dispatch(hideModal())}
                      onSend={(clientIds, customMessage) => {
                        dispatch(handleSendOnboardingEmails(clientIds, customMessage));
                        dispatch(hideModal());
                      }}
                    />
                  )}

                  {modalState.type === 'editMatterData' && modalState.onConfirm && (
                    <EditCaseDataModalContent
                      item={modalState.item}
                      onConfirm={(data) => modalState.onConfirm!(data)}
                      onCancel={handleClose}
                    />
                  )}

                  {modalState.type === 'addSource' && (
                    <AddSourceModalContent
                      sourceType={modalState.sourceType as 'law' | 'case' || 'law'}
                      onConfirm={(sourceData) => {
                        modalState.onConfirm!(sourceData);
                        dispatch(hideModal());
                      }}
                      onCancel={() => dispatch(hideModal())}
                    />
                  )}

                  {modalState.type === 'attachExistingCaseToClient' && (
                    <AttachCaseToClientModalContent
                      clientId={modalState.clientId as string}
                      onClose={handleClose}
                    />
                  )}


                  {modalState.type === 'forgotPassword' && (
                    <>
                      <div className='px-4'>
                        <Input
                          type="email"
                          placeholder={t('Enter your email')}
                          value={modalState.inputData ? modalState.inputData : email}
                          onChange={(e) => setEmail(e.target.value)}
                          id={''}
                        />
                      </div>
                      <div className='flex justify-end mt-9 pt-6 border-t border-1 border-gray-300 px-4'>
                        <Button variant="primary" size='small' onClick={handleConfirm}>
                          {t('Send Reset Link')}
                        </Button>
                      </div>
                    </>
                  )}

                  {modalState.type === 'addMember' && (
                    <TeamActionModal
                      action={modalState.action as 'AddMember'}
                      existingMembers={modalState.existingMembers}
                      selectedValue={modalState.selectedValue as string[]}
                      onConfirm={modalState.onConfirm!}
                      onCancel={modalState.onCancel || handleClose}
                    />
                  )}

                  {modalState.type === 'linkCase' && (
                    <TeamActionModal
                      action={modalState.action as 'LinkCase'}
                      selectedValue={modalState.selectedValue as string}
                      onConfirm={modalState.onConfirm!}
                      onCancel={modalState.onCancel || handleClose}
                    />
                  )}

                  {/* Tips */}
                  {modalState.type === 'tips' && (
                    <>
                      <p className="text-sm text-gray-500 dark:text-gray-300">{modalState.info}</p>
                      <Button variant="primary" size='small' onClick={handleClose} className="mt-4 w-full">
                        {t('Close')}
                      </Button>
                    </>
                  )}

                  {modalState.type === 'info' && (
                    <>
                      <h1 className="text-lg font-medium text-gray-900 dark:text-gray-100">{modalState.title}</h1>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500 dark:text-gray-300" dangerouslySetInnerHTML={{ __html: modalState.info! }}></p>
                      </div>
                    </>
                  )}

                  {modalState.type === 'addTask' && (
                    <AddTask />
                  )}

                  {modalState.type === 'suggestedTasks' && (
                    <SuggestedTasksModal
                      tasks={modalState.tasks || []}
                      onConfirm={modalState.onConfirm}
                      onCancel={handleClose}
                      matterUuid={modalState.matterUuid}
                    />
                  )}

                  {modalState.type === 'importSummary' && (
                    <ImportSummaryModal summary={modalState.summaryData as ImportSummary} />
                  )}

                  {modalState.type === 'password' && (
                    <>
                      <Input
                        type="password"
                        placeholder={t('Enter your password')}
                        value={inputData}
                        onChange={(e) => setInputData(e.target.value)}
                        className="w-full mb-4" id={''} />
                      <Button variant="primary" size='small' onClick={handleConfirm} className="w-full">
                        {t('Save Changes')}
                      </Button>
                    </>
                  )}

                  {modalState.type === 'promptfeedback' && (
                    <>
                      <p className="text-sm text-gray-500 dark:text-gray-300">{t('Please let us know what went wrong.')}</p>
                      <Textarea
                        placeholder={t('Please provide a feedback on what went wrong')}
                        value={inputData}
                        onChange={(e) => setInputData(e.target.value)}
                        id={''}
                        label={t('Feedback')}
                      />
                      <Button variant="primary" size='small' onClick={handleConfirm} className="w-full">
                        {t('Send feedback')}
                      </Button>
                    </>
                  )}


                  {modalState.type === 'importClients' && <ImportClientsModal onClose={handleClose} />}

                  {modalState.type === 'confirmation' && (
                    isConfirming ? (
                      <div className="flex flex-col items-center justify-center py-10 px-4 gap-4">
                        <Spinner size="lg" />
                        <div className="text-center space-y-1.5">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {t('Working on it...')}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t('Please wait, this may take a moment.')}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-4 max-w-3xl">
                        {modalState.content ? (
                          <div className="px-4 text-start">{modalState.content}</div>
                        ) : (
                          // Canonical (#2332): `message` is the body, `subText` the muted
                          // secondary line. Legacy callers put the body in `subMsg`.
                          ((modalState.title ? modalState.message : modalState.subMsg) || modalState.subText) && (
                            <div className="px-4 text-start space-y-1.5">
                              {(modalState.title ? modalState.message : modalState.subMsg) && (
                                <p className="text-sm text-gray-500 dark:text-gray-400" dangerouslySetInnerHTML={{ __html: t((modalState.title ? modalState.message : modalState.subMsg) || '') }}></p>
                              )}
                              {modalState.title && modalState.subText && (
                                <p className="text-xs text-gray-400 dark:text-gray-500" dangerouslySetInnerHTML={{ __html: t(modalState.subText) }}></p>
                              )}
                            </div>
                          )
                        )}
                        <div className="flex justify-end space-x-4 px-4 mt-6 pt-6 border-t border-gray-300 border-1">
                          <Button variant="neutral" size='small' onClick={handleClose} className='w-24'>
                            {t(modalState.cancelLabel || 'Cancel')}
                          </Button>
                          <Button
                            variant={isDanger ? 'destructive' : 'primary'}
                            size='small'
                            onClick={async () => {
                              if (!modalState.onConfirm) return;
                              try {
                                setIsConfirming(true);
                                await Promise.resolve(modalState.onConfirm(inputData));
                              } finally {
                                setIsConfirming(false);
                                // The confirm path owns the dismissal - without this
                                // every confirmation modal stayed open after the action.
                                // `dismiss` (not `handleClose`) so onCancel doesn't fire.
                                dismiss();
                              }
                            }}
                            className='w-24'
                          >
                            {t(modalState.confirmLabel || 'Confirm')}
                          </Button>
                        </div>
                      </div>
                    )
                  )}

                  {['success', 'error', 'warning', 'feedback'].includes(modalState.type) && (
                    <div className="mt-5 sm:mt-6">
                    </div>
                  )}

                  {modalState.type === 'alert' && (
                    <div className="mt-5 sm:mt-6 flex justify-end space-x-4">
                      <Button size='small' variant="primary" onClick={handleConfirm}>
                        {modalState.inputData ? modalState.inputData : t('Yes, Sign out')}
                      </Button>
                    </div>
                  )}

                  {modalState.type === 'ai-settings' && (
                    <AISettingsComponent />
                  )}


                  {modalState.type === 'confirm-upload' && (
                    <div className="flex flex-col space-y-4">
                      <Button variant="primary" size='small' onClick={() => modalState.onConfirm && modalState.onConfirm('append')}>
                        {t('Append')}
                      </Button>
                      <Button variant="primary" size='small' onClick={() => modalState.onConfirm && modalState.onConfirm('replace')}>
                        {t('Replace')}
                      </Button>
                    </div>
                  )}

                  {modalState.type === 'custom' && modalState.content && (
                    <div>{modalState.content}</div>
                  )}

                  {modalState.type === 'citations' && modalState.citations && (
                    <div className="space-y-4">
                      {modalState.citations.map((citation: Citation, index: number) => (
                        <div key={index} className="border-b border-gray-200 dark:border-gray-700 pb-4">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                              {citation.id}
                            </span>
                          </div>
                          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                            {citation.snippet}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}


                  {modalState.type === 'addTextContent' && (
                    <div className="min-w-[600px] sm:w-full">
                      <div className='px-4'>
                        <Textarea
                          value={inputData}
                          onChange={(e) => setInputData(e.target.value)}
                          placeholder={t('Enter your text content here...')}
                          rows={6}
                          className="w-full mb-4" id={''} label={''}
                        />
                      </div>
                      <div className="mt-6 pt-6 border-t border-1 border-gray-300 flex justify-end gap-2 px-4">
                        <Button
                          variant="primary"
                          size="small"
                          onClick={handleConfirm}
                          disabled={!inputData.trim()}
                        >
                          {t('Add Content')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {modalState.type === 'citation' && modalState.citation && (
                    <div className='max-w-2xl'>
                      <h4 className="text-lg font-semibold text-gray-700 dark:text-gray-200">
                        {modalState.citation.id}
                      </h4>
                      <hr className="my-2 border-gray-300 dark:border-gray-700" />
                      <ReactMarkdown
                        className="markdown-content"
                        components={{
                          p: ({ node, ...props }) => <p className="mb-4" {...props} />,
                        }}
                      >
                        {modalState.citation.snippet}
                      </ReactMarkdown>
                    </div>
                  )}

                  {modalState.type === 'vaultFilePicker' && (
                    <VaultPicker onFilesSelected={handleVaultFilesSelected} />
                  )}

                  {modalState.type === 'timeStop' && (
                    <TimeStopModalContent
                      onConfirm={(data) => {
                        if (modalState.onConfirm) {
                          modalState.onConfirm(data);
                        }
                      }}
                      onClose={handleClose}
                    />
                  )}

                  {
                    modalState.type === 'sign_doc' && (
                      <>
                        <SignatureModal
                          onSave={() => { }}
                          onConfirm={() => { }}
                        />
                        <div className="w-full flex justify-end space-x-4 mt-4">
                          <Button variant="primary" size='small' onClick={() => modalState.onConfirm && modalState.onConfirm(modalState.fileID)}>
                            {t('Sign')}
                          </Button>
                          <Button variant="secondary" size='small' onClick={() => handleClose()}>
                            {t('Close')}
                          </Button>
                        </div>
                      </>
                    )
                  }


                  {modalState.type === 'redirect' && (
                    <div className="fixed inset-0 bg-ink/20 backdrop-blur-[2px] flex justify-center items-center z-50">
                      <div className="glass dark:text-gray-200 rounded-lg shadow-lg p-6 w-full max-w-md transition-all">

                        <div className="flex justify-between items-center border-b pb-3 dark:border-gray-700">
                          <h2 className="text-lg font-semibold flex items-center gap-2">
                            <AiOutlineInfoCircle className="text-info-600 dark:text-info-400" />
                            {t("Redirect Notice")}
                          </h2>
                          <button
                            onClick={() => handleClose()}
                            className="text-gray-500 hover:text-danger-500 transition"
                          >
                            <AiOutlineClose className="w-5 h-5" />
                          </button>
                        </div>

                        <div className="mt-4">
                          <p className="text-gray-700 dark:text-gray-300">{t(modalState.message)}</p>
                          <div className="mt-3 p-3 bg-gray-100 dark:bg-gray-700 rounded-md text-sm">
                            <span className="text-gray-600 dark:text-gray-300">{t("Redirecting to:")}</span>
                            <p className="break-all font-medium text-info-600 dark:text-info-400">{modalState.inputData}</p>
                          </div>
                        </div>

                        <div className="mt-5 flex justify-end gap-2">
                          <button
                            className="px-4 py-2 rounded-md bg-info-600 text-white flex items-center gap-2 hover:bg-info-700 transition"
                            onClick={() => {
                              modalState.inputData && window.location.assign(modalState.inputData);
                              handleClose()
                            }}
                          >
                            {t("Continue")} <FaArrowRight />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {modalState.type === 'receiver-options' && (
                    <BankFormItems />
                  )}

                  {modalState.type === 'addTaskComment' && (
                    <div className=''>
                      <div className='px-4'>
                        <Textarea
                          placeholder={t("Write your comment here...")}
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          id={''}
                          label={t('Comment')}
                        />
                      </div>
                      <div className="flex justify-end mt-6 pt-6 space-x-2 border-t border-1 border-gray-300 px-4">
                        <Button
                          // className={`${comment.trim() ? "" : "bg-gray-300 cursor-not-allowed"
                          //   }`}
                          variant='primary'
                          size='small'
                          onClick={() => modalState.onConfirm && modalState.onConfirm(comment.trim())}
                          // disabled={!comment.trim()}
                        >
                          {t("Submit")}
                        </Button>
                      </div>
                    </div>



                  )}

                  {modalState.type === 'giveAccess' && (
                    <ShareAccessManager onSave={(data) => modalState.onSave && modalState.onSave(data)} employees={modalState.employees} initialShared={modalState.initialShared} onCancel={handleClose} collaboratorRoles={modalState.roles} />
                  )}

                  {modalState.type === 'manageAccess' && (
                    <ManageAccessModal
                      itemId={modalState.itemId || ''}
                      itemType={modalState.itemType || 'file'}
                      itemName={modalState.itemName || ''}
                      itemCollaborators={modalState.itemCollaborators || []}
                      creatorId={modalState.creatorId}
                    />
                  )}

                  {modalState.type === 'editDepartment' && modalState.department && (
                    <div className="min-w-[700px] sm:w-full">
                      <EditDepartmentModal
                        ref={editDepartmentRef}
                        department={modalState.department}
                        onSave={async (data) => {
                          if (modalState.onSave) {
                            await Promise.resolve(modalState.onSave(data));
                          }
                        }}
                        onAfterSave={modalState.onAfterSave ? async () => {
                          await Promise.resolve(modalState.onAfterSave!());
                        } : undefined}
                      />
                      <div className="mt-6 pt-6 border-t border-1 border-gray-300 dark:border-gray-700 flex justify-end gap-2 px-6">
                        <Button
                          variant="primary"
                          size="small"
                          onClick={async () => {
                            if (editDepartmentRef.current) {
                              try {
                                setEditDepartmentSaving(true);
                                await editDepartmentRef.current.handleSave();
                                handleClose();
                              } catch (err) {
                                console.error('Error saving department:', err);
                              } finally {
                                setEditDepartmentSaving(false);
                              }
                            }
                          }}
                          disabled={editDepartmentSaving}
                        >
                          {editDepartmentSaving ? t('Saving...') : t('Save')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {modalState.type === 'editTeam' && modalState.team && (
                    <div className="min-w-[700px] max-w-[700px] sm:w-full">
                      <EditTeamModal
                        ref={editTeamRef}
                        team={modalState.team}
                        onSave={async (data) => {
                          if (modalState.onSave) {
                            await Promise.resolve(modalState.onSave(data));
                          }
                        }}
                        onAfterSave={modalState.onAfterSave ? async () => {
                          await Promise.resolve(modalState.onAfterSave!());
                        } : undefined}
                      />
                      <div className="mt-6 pt-6 border-t border-1 border-gray-300 dark:border-gray-700 flex justify-end gap-2 px-6">
                        <Button
                          variant="primary"
                          size="small"
                          onClick={async () => {
                            if (editTeamRef.current) {
                              try {
                                setEditTeamSaving(true);
                                await editTeamRef.current.handleSave();
                                handleClose();
                              } catch (err) {
                                console.error('Error saving team:', err);
                              } finally {
                                setEditTeamSaving(false);
                              }
                            }
                          }}
                          disabled={editTeamSaving}
                        >
                          {editTeamSaving ? t('Saving...') : t('Save')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {modalState.type === 'editRateOverride' && (
                    <div className="min-w-[34rem] sm:w-full">
                      <EditRateOverrideModal
                        ref={editRateOverrideRef}
                        rateOverride={modalState.rateOverride}
                        collaborators={modalState.collaborators || []}
                        defaultCurrency={modalState.defaultCurrency || 'EUR'}
                        onValidityChange={setEditRateOverrideValid}
                        onSave={async (data) => {
                          if (modalState.onSave) {
                            await Promise.resolve(modalState.onSave(data));
                          }
                        }}
                        onAfterSave={modalState.onAfterSave ? async () => {
                          await Promise.resolve(modalState.onAfterSave!());
                        } : undefined}
                      />
                      <div className="mt-6 pt-6 border-t border-1 border-gray-300 dark:border-gray-700 flex justify-end gap-2 px-6">
                        <Button
                          variant="primary"
                          size="small"
                          onClick={async () => {
                            if (editRateOverrideRef.current) {
                              try {
                                setEditRateOverrideSaving(true);
                                await editRateOverrideRef.current.handleSave();
                                handleClose();
                              } catch (err) {
                                // Validation errors keep the modal open; surfaced inline.
                              } finally {
                                setEditRateOverrideSaving(false);
                              }
                            }
                          }}
                          disabled={editRateOverrideSaving || !editRateOverrideValid}
                        >
                          {editRateOverrideSaving ? t('Saving...') : t('Save')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {modalState.type === 'editTimeEntry' && (
                    <div className="min-w-[34rem] sm:w-full">
                      <EditTimeEntryModal
                        ref={editTimeEntryRef}
                        timeEntry={modalState.timeEntry}
                        activityTypes={modalState.activityTypes || []}
                        collaborators={modalState.collaborators || []}
                        defaultUserUuid={modalState.defaultUserUuid}
                        onValidityChange={setEditTimeEntryValid}
                        onSave={async (data) => {
                          if (modalState.onSave) {
                            await Promise.resolve(modalState.onSave(data));
                          }
                        }}
                        onAfterSave={modalState.onAfterSave ? async () => {
                          await Promise.resolve(modalState.onAfterSave!());
                        } : undefined}
                      />
                      <div className="mt-6 pt-6 border-t border-1 border-gray-300 dark:border-gray-700 flex justify-end gap-2 px-6">
                        <Button
                          variant="primary"
                          size="small"
                          onClick={async () => {
                            if (editTimeEntryRef.current) {
                              try {
                                setEditTimeEntrySaving(true);
                                await editTimeEntryRef.current.handleSave();
                                handleClose();
                              } catch (err) {
                                // Validation errors keep the modal open; surfaced inline.
                              } finally {
                                setEditTimeEntrySaving(false);
                              }
                            }
                          }}
                          disabled={editTimeEntrySaving || !editTimeEntryValid}
                        >
                          {editTimeEntrySaving ? t('Saving...') : t('Save')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {modalState.type === 'matterOutcome' && (
                    <div className="min-w-[34rem] sm:w-full">
                      <OutcomeModal
                        ref={outcomeRef}
                        mode={modalState.outcomeMode || 'close'}
                        taxonomy={modalState.taxonomy || []}
                        outcome={modalState.outcome}
                        defaultCurrency={modalState.defaultCurrency || 'usd'}
                        onValidityChange={setOutcomeValid}
                        onSave={async (data) => {
                          if (modalState.onSave) {
                            await Promise.resolve(modalState.onSave(data));
                          }
                        }}
                        onAfterSave={modalState.onAfterSave ? async () => {
                          await Promise.resolve(modalState.onAfterSave!());
                        } : undefined}
                      />
                      <div className="mt-6 pt-6 border-t border-1 border-gray-300 dark:border-gray-700 flex justify-end gap-2 px-6">
                        <Button
                          variant="primary"
                          size="small"
                          onClick={async () => {
                            if (outcomeRef.current) {
                              try {
                                setOutcomeSaving(true);
                                await outcomeRef.current.handleSave();
                                handleClose();
                              } catch (err) {
                                // Validation errors keep the modal open; surfaced inline.
                              } finally {
                                setOutcomeSaving(false);
                              }
                            }
                          }}
                          disabled={outcomeSaving || !outcomeValid}
                        >
                          {outcomeSaving
                            ? t('Saving...')
                            : modalState.outcomeMode === 'edit'
                              ? t('Save')
                              : t('Close Matter')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {
                    modalState.type === 'fileUploader' && (
                      <FileUploader onFilesChange={(files) => {
                        modalState?.onFilesChange?.(files)
                      }} files={modalState.files || []} />

                    )
                  }

                  {
                    modalState.type === "confirmInvitation" && (
                      <>
                        <div className="text-gray-700 dark:text-gray-200 text-sm space-y-3">
                          <p>
                            Your invitation is ready.
                          </p>

                          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                            <p className="text-base font-semibold text-gray-900 dark:text-white">
                              John Doe
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              john.doe@mail.com
                            </p>
                          </div>

                          <p>
                            Do you want to send them an invitation to collaborate on this matter?
                          </p>
                        </div>

                        <div className="flex justify-end mt-4 space-x-2">
                          <Button
                            className={`bg-gray-300 cursor-not-allowed `}
                            variant='primary'
                            size='small'
                            onClick={() => modalState.onConfirm && modalState.onConfirm(comment.trim())}
                          >
                            {t("Send")}
                          </Button>
                        </div>
                      </>
                    )
                  }

                  {modalState.type === 'connectMatter' && (
                    <div className="min-w-[600px] sm:w-full">
                      <ConnectMatterModal clientId={modalState.clientUuid!} clientName={modalState.clientName as string} selectedMatterId={selectedMatterId} onChange={setSelectedMatterId} selectedRole={selectedRole} onRoleChange={setSelectedRole} />
                      <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700 mt-6 px-4">
                        <Button
                          variant="primary"
                          size="small"
                          onClick={async () => {
                            if (!selectedMatterId || !modalState.clientUuid) return;
                            try {
                              setConnectMatterLoading(true);
                              const resp = await apis({
                                method: 'POST',
                                url: `/clients/${modalState.clientUuid}/matters`,
                                data: { matter_uuid: selectedMatterId, role: selectedRole },
                              });
                              if (resp.status === 200 || resp.status === 201) {
                                modalState.onMatterConnected?.();
                                handleClose();
                              }
                            } catch (err) {
                              console.error('Error connecting matter:', err);
                            } finally {
                              setConnectMatterLoading(false);
                              setSelectedMatterId('');
                              setSelectedRole('');
                            }
                          }}
                          disabled={connectMatterLoading || !selectedMatterId || !selectedRole}
                        >
                          {connectMatterLoading ? t('Connecting...') : t('Connect Matter')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {modalState.type === 'uploadClientDocument' && (
                    <div className="min-w-[600px] max-w-[600px] sm:w-full">
                      <div className='px-4 mb-4'>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {t('For')} <span className="font-medium">{modalState.clientName}</span>
                        </p>
                      </div>
                      <DocumentUploadModalContent
                        clientId={modalState.clientUuid!}
                        clientName={modalState.clientName}
                        onClose={handleClose}
                        onUploadComplete={() => {
                          modalState.onDocumentUploadComplete?.();
                          handleClose();
                        }}
                      />
                    </div>
                  )}

                  {modalState.type === 'adminCreateRole' && (
                    <AdminRoleModal
                      mode="create"
                      orgType={modalState.orgType}
                      onConfirm={(roleData) => {
                        modalState.onConfirm?.(roleData);
                        handleClose();
                      }}
                      onCancel={handleClose}
                    />
                  )}

                  {modalState.type === 'adminEditRole' && modalState.role && (
                    <AdminRoleModal
                      mode="edit"
                      role={modalState.role}
                      orgType={modalState.orgType}
                      onConfirm={(roleData) => {
                        modalState.onConfirm?.(roleData);
                        handleClose();
                      }}
                      onCancel={handleClose}
                    />
                  )}

                  {modalState.type === 'adminAssignRole' && (
                    <AdminRoleDetailModal
                      roleId={modalState.roleId || ''}
                      onConfirm={(userId) => {
                        modalState.onConfirm?.(userId);
                        handleClose();
                      }}
                      onCancel={handleClose}
                    />
                  )}

                  {modalState.type === 'adminEditUser' && modalState.user && (
                    <AdminUserModal
                      user={modalState.user}
                      adminCount={modalState.item?.adminCount || 0}
                      onConfirm={(userData) => {
                        modalState.onConfirm?.(userData);
                        handleClose();
                      }}
                      onCancel={handleClose}
                    />
                  )}

                  {modalState.type === 'adminInviteUser' && (
                    <AdminInviteUserModal
                      onConfirm={() => {
                        if (modalState.onConfirm) {
                          modalState.onConfirm(undefined);
                        }
                        handleClose();
                      }}
                      onCancel={handleClose}
                    />
                  )}

                  {modalState.type === 'addClientToMatter' && (
                    <AddClientToMatterModal
                      matterUuid={modalState.matterUuid || ''}
                      clientRoleOptions={modalState.clientRoleOptions || []}
                      existingMatterClients={(modalState as any).existingMatterClients || []}
                      onClientAdded={async () => {
                        if (modalState.onRefresh) {
                          await modalState.onRefresh();
                        }
                        if (modalState.onAfterSave) {
                          await modalState.onAfterSave();
                        }
                      }}
                    />
                  )}

                  {modalState.type === 'versionHistory' && (
                    <div className="px-4">
                      <VersionHistory
                        fileId={modalState.fileId || ''}
                        folderId={modalState.folderId || ''}
                        fileName={modalState.fileName || ''}
                        onClose={handleClose}
                        onVersionRestore={modalState.onVersionRestore}
                      />
                    </div>
                  )}

                                    {modalState.type === 'license-request' && (
                    <LicenseGateModal
                      title={modalState.title || t('Upgrade Required')}
                      description={modalState.subMsg || t('Your current plan does not include this feature.')}
                      planName={typeof modalState.content === 'string' ? modalState.content : undefined}
                      category={modalState.category}
                      section={modalState.inputData}
                      onViewPlans={() => {
                        if (modalState.onConfirm) modalState.onConfirm(null);
                      }}
                      onClose={handleClose}
                    />
  )}

                  {modalState.type === 'templateCollaborators' && (
                    <TemplateCollaboratorsModalContent
                      collaborators={modalState.collaborators || []}
                      onCollaboratorsChange={modalState.onCollaboratorsChange || (() => {})}
                      onClose={handleClose}
                    />
                  )}

                  {modalState.type === 'templatePreview' && (
                    <TemplatePreviewModalContent
                      branding={modalState.templateBranding}
                      onClose={handleClose}
                    />
                  )}

                  {modalState.type === 'uploadTableDocuments' && (
                    <div className='w-[50vw] max-w-[50vw] min-w-[50vw] relative'>
                      <div className='px-4'>
                        <FileUploader
                          files={files}
                          onFilesChange={(newFiles) => setFiles(newFiles)}
                          allowedExtensions={['pdf', 'doc', 'docx', 'xlsx', 'xls', 'txt', 'csv']}
                        />
                      </div>
                      <div className="mt-6 pt-6 border-t border-1 border-gray-300 flex justify-end gap-2 px-4">
                        <Button
                          variant="primary"
                          size="small"
                          onClick={() => handleConfirm()}
                          disabled={files.length === 0}
                        >
                          {t('Upload')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {modalState.type === 'addTableColumn' && (
                    <AddTableColumnContent
                      onConfirm={(data) => {
                        if (modalState.onConfirm) modalState.onConfirm(data);
                        handleClose();
                      }}
                      onCancel={handleClose}
                    />
                  )}

                  {modalState.type === 'editTableColumn' && modalState.columnToEdit && (
                    <EditColumnContent
                      column={modalState.columnToEdit}
                      onConfirm={(payload, queryChanged) => {
                        if (modalState.onConfirm) modalState.onConfirm(payload, queryChanged);
                      }}
                      onCancel={handleClose}
                    />
                  )}

                  {modalState.type === 'reviewHelp' && (
                    <div className="w-[680px] max-w-[90vw]">
                      <ReviewHelpGuide />
                    </div>
                  )}

                  {modalState.type === 'playbookHelp' && (
                    <div className="w-[680px] max-w-[90vw]">
                      <PlaybookHelpGuide />
                    </div>
                  )}

                  {modalState.type === 'replacePlaybookDocument' && (
                    <div className="w-[50vw] max-w-[50vw] min-w-[50vw] relative">
                      <div className="px-4">
                        <FileUploader
                          files={files}
                          onFilesChange={setFiles}
                          allowedExtensions={modalState.allowedExtensions || ['pdf', 'docx', 'doc', 'txt']}
                          maxFiles={modalState.maxFiles || 1}
                          {...(modalState.maxSizeBytes ? { maxSizeBytes: modalState.maxSizeBytes } : {})}
                        />
                      </div>
                      <div className="flex justify-end gap-3 px-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button variant="neutral" size="small" onClick={handleClose}>
                          {t('Cancel')}
                        </Button>
                        <Button
                          variant="primary"
                          size="small"
                          disabled={files.length === 0}
                          onClick={() => {
                            if (files.length > 0 && modalState.onFileSelected) {
                              modalState.onFileSelected(files[0]);
                            }
                            handleClose();
                          }}
                        >
                          {modalState.confirmLabel || t('Replace & Extract')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {modalState.type === 'tagPicker' && (
                    <TagPickerModal
                      tags={modalState.tags || []}
                      fileId={modalState.fileId}
                      folderId={modalState.folderId}
                      onTagsChanged={modalState.onTagsChanged}
                      onClose={handleClose}
                    />
                  )}

                  {modalState.type === 'documentInfo' && modalState.documentInfo && (
                    <DocumentInfoModal info={modalState.documentInfo} />
                  )}

                  {modalState.type === 'auditLogDetail' && modalState.auditEntry && (
                    <AuditLogDetailModal
                      entry={modalState.auditEntry}
                      onClose={handleClose}
                    />
                  )}

                  {modalState.type === 'researchShare' && modalState.chatUuid && (
                    <ResearchShareModal
                      chatUuid={modalState.chatUuid}
                      chatTitle={modalState.chatTitle || ''}
                      onShared={modalState.onConfirm ? () => modalState.onConfirm!(undefined) : undefined}
                    />
                  )}

                  {modalState.type === 'draftingAttachMatter' && modalState.draftingUuid && (
                    <DraftingMatterPickerModal
                      draftingKind={(modalState.draftingKind ?? 'draft') as any}
                      draftingUuid={modalState.draftingUuid}
                      existingMatterIds={modalState.existingMatterIds ?? []}
                      onAttached={modalState.onConfirm ? () => modalState.onConfirm!(undefined) : undefined}
                    />
                  )}

                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default GlobalModal;
